import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { CreditCard, Shield, LogOut, Sparkles, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png";

export default function PaymentPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = async () => {
    setLoading(true);
    setError("");
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API}/payments/checkout`,
        { origin_url: origin },
        { withCredentials: true }
      );
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error al procesar el pago");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (user?.has_paid) {
    navigate("/app/feed");
    return null;
  }

  const features = [
    "Acceso completo al feed de la comunidad",
    "Salas Refugium (acompañante y acompañad@)",
    "Participación en Voces Refugium",
    "Solicitud de sesiones individualizadas",
    "Eventos, talleres y directos exclusivos",
  ];

  return (
    <div className="min-h-screen bg-[#F5EEDB] flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Refugium" className="w-16 h-16 rounded-full mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-[#5B3E2E]">Únete a Refugium</h1>
          <p className="text-sm text-[#5B3E2E]/60 mt-2">Cancela cuando quieras.</p>
        </div>

        {error && (
          <div data-testid="payment-error" className="bg-[#D9534F]/10 text-[#D9534F] text-sm rounded-xl p-3 mb-4">
            {error}
          </div>
        )}

        <div className="bg-[#FDFAF4] rounded-3xl p-6 border border-[#5B3E2E]/10 shadow-[0_8px_30px_rgba(91,62,46,0.06)]">
          <div className="inline-flex items-center gap-2 bg-[#6B9070]/10 rounded-full px-3 py-1.5 mb-4">
            <Sparkles size={14} className="text-[#6B9070]" />
            <span className="text-xs font-semibold text-[#6B9070]">Cancela cuando quieras</span>
          </div>

          <div className="mb-5">
            <div className="flex items-baseline gap-1">
              <span className="font-heading text-5xl text-[#5B3E2E] font-bold">7,90</span>
              <span className="text-[#5B3E2E]/60">€/mes</span>
            </div>
            <p className="text-xs text-[#5B3E2E]/50 mt-1">IVA incluido · sin permanencia</p>
          </div>

          <ul className="space-y-2.5 mb-6">
            {features.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#5B3E2E]/80">
                <div className="w-5 h-5 rounded-full bg-[#6B9070]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={12} className="text-[#6B9070]" />
                </div>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <Button
            data-testid="checkout-btn"
            onClick={handleCheckout}
            disabled={loading}
            className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3.5 font-medium transition-all flex items-center justify-center gap-2 shadow-[0_4px_16px_rgba(217,120,51,0.3)]"
          >
            <CreditCard size={18} />
            {loading ? "Redirigiendo…" : "Suscribirme por 7,90€/mes"}
          </Button>

          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-[#5B3E2E]/40">
            <Shield size={12} /> Pago seguro con Stripe
          </div>
        </div>

        <button
          data-testid="payment-logout"
          onClick={handleLogout}
          className="flex items-center justify-center gap-2 mx-auto mt-6 text-sm text-[#5B3E2E]/50 hover:text-[#5B3E2E]/80 transition-colors"
        >
          <LogOut size={14} />
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
